import React, { useState } from 'react';
import '../styles/inspire.css';

const Admin = () => {
  const [password, setPassword] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Login to get JWT token
      const loginRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        }
      );

      if (!loginRes.ok) {
        const errData = await loginRes.json().catch(() => ({}));
        throw new Error(errData.detail || 'Invalid password');
      }

      const loginData = await loginRes.json();
      const jwt = loginData.token;
      setToken(jwt);

      // Fetch submissions with JWT
      const subRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions`,
        { headers: { 'Authorization': `Bearer ${jwt}` } }
      );

      if (!subRes.ok) {
        throw new Error('Failed to fetch submissions');
      }

      const data = await subRes.json();
      setSubmissions(data.submissions);
      setTotal(data.total);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.message);
      setIsAuthenticated(false);
      setToken('');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${submissionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete submission');
      }

      setSubmissions(submissions.filter(s => s.id !== submissionId));
      setTotal(total - 1);
    } catch (err) {
      alert('Error deleting submission: ' + err.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setToken('');
    setSubmissions([]);
    setTotal(0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--bg-cream)', 
      padding: '2rem',
      fontFamily: "'DM Sans', sans-serif"
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <h1 style={{ 
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '2rem',
          marginBottom: '2rem',
          background: 'linear-gradient(90deg, #F9C8A8 0%, #E6A9C0 50%, #C4A8E2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Admin - Contact Submissions
        </h1>

        {!isAuthenticated ? (
          <form onSubmit={handleSubmit} style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px',
            boxShadow: '0 2px 20px rgba(0,0,0,0.03)',
            maxWidth: '400px'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              color: '#3D3D3D'
            }}>
              Enter Admin Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '1px solid #E8E4E0',
                borderRadius: '8px',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            />
            <button
              type="submit"
              disabled={isLoading}
              data-testid="admin-login-button"
              style={{
                background: 'linear-gradient(90deg, #F9C8A8 0%, #E6A9C0 50%, #C4A8E2 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              {isLoading ? 'Loading...' : 'View Submissions'}
            </button>
            {error && (
              <p data-testid="admin-error-message" style={{ color: '#e74c3c', marginTop: '1rem', fontSize: '0.9rem' }}>
                {error}
              </p>
            )}
          </form>
        ) : (
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <p style={{ color: '#6B6B6B' }}>
                Total Submissions: <strong>{total}</strong>
              </p>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-button"
                style={{
                  background: 'transparent',
                  border: '1px solid #E8E4E0',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Logout
              </button>
            </div>

            {submissions.length === 0 ? (
              <div style={{ 
                background: 'white', 
                padding: '3rem', 
                borderRadius: '12px',
                textAlign: 'center',
                color: '#6B6B6B'
              }}>
                No submissions yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {submissions.map((sub, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      boxShadow: '0 2px 20px rgba(0,0,0,0.03)',
                      borderLeft: '3px solid',
                      borderImage: 'linear-gradient(180deg, #F9C8A8 0%, #E6A9C0 50%, #C4A8E2 100%) 1'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      <h3 style={{ 
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: '1.25rem',
                        margin: 0
                      }}>
                        {sub.name}
                      </h3>
                      <span style={{ 
                        fontSize: '0.8rem', 
                        color: '#6B6B6B' 
                      }}>
                        {formatDate(sub.timestamp)}
                      </span>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.75rem',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <div>
                        <span style={{ color: '#6B6B6B' }}>Email: </span>
                        <a href={`mailto:${sub.email}`} style={{ color: '#E6A9C0' }}>
                          {sub.email}
                        </a>
                      </div>
                      {sub.phone && (
                        <div>
                          <span style={{ color: '#6B6B6B' }}>Phone: </span>
                          <a href={`tel:${sub.phone}`} style={{ color: '#E6A9C0' }}>
                            {sub.phone}
                          </a>
                        </div>
                      )}
                      <div>
                        <span style={{ color: '#6B6B6B' }}>Preferred: </span>
                        {sub.preferredContact}
                      </div>
                    </div>
                    <div style={{ 
                      background: '#FDF9F6', 
                      padding: '1rem',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      lineHeight: '1.6'
                    }}>
                      {sub.message}
                    </div>
                    <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                      <button
                        onClick={() => deleteSubmission(sub.id)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #e74c3c',
                          color: '#e74c3c',
                          padding: '0.4rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#e74c3c';
                          e.target.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#e74c3c';
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
